import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  SxProps,
  TextFieldProps,
} from '@mui/material';

import { Close as CloseIcon } from '@mui/icons-material';

import { TPTextField } from '../../../../../../components/Inputs';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import {
  adminCreateRedirection,
  adminUpdateRedirection,
  IRedirection,
  TAdminCreateRedirection,
  TAdminUpdateRedirection,
} from '../../../../../../store/V2/RedirectionsSlice';

const dialogTitleStyles: SxProps = {
  m: 0,
  p: { xs: '15px', sm: '30px' },
  color: '#000000',
  fontWeight: 600,
  fontSize: '16px',
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

interface AddRedirectionModalProps {
  open: boolean;
  onClose: () => void;
  redirection?: IRedirection;
  readOnly?: boolean;
  duplicate?: boolean;
}

type TState = {
  oldUrl: string;
  newUrl: string;
};

const initialState: TState = {
  oldUrl: '',
  newUrl: '',
};

const AddRedirectionModal: React.FC<AddRedirectionModalProps> = ({
  open,
  onClose,
  redirection,
  readOnly = false,
  duplicate = false,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  let mode: 'edit' | 'new' | 'view' | 'duplicate' = 'new';
  let modalTitle = 'Add New Redirection';

  const isSubmitDisabled =
    !state.oldUrl.trim().length || !state.newUrl.trim().length;

  if (redirection) {
    mode = 'edit';
    modalTitle = 'Edit Redirection';

    if (readOnly) {
      mode = 'view';
      modalTitle = 'Redirection Details';
    } else if (duplicate) {
      mode = 'duplicate';
      modalTitle = 'Add New Redirection';
    }
  }

  useEffect(() => {
    if (mode !== 'new' && redirection) {
      setState((prevState) => {
        const newStateData = { ...prevState };

        if (redirection.oldUrl) {
          newStateData.oldUrl = redirection.oldUrl;
        }

        if (redirection.newUrl) {
          newStateData.newUrl = redirection.newUrl;
        }

        return newStateData;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (mode === 'new' || mode === 'duplicate') {
      const apiData: TAdminCreateRedirection = {
        oldUrl: state.oldUrl,
        newUrl: state.newUrl,
      };

      await dispatch(adminCreateRedirection(apiData));

      onClose();
    } else if (mode === 'edit' && redirection) {
      const apiData: TAdminUpdateRedirection = {
        _id: redirection._id,
        oldUrl: state.oldUrl,
        newUrl: state.newUrl,
      };

      await dispatch(adminUpdateRedirection(apiData));

      onClose();
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle sx={dialogTitleStyles}>{modalTitle}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  label="Old URL *"
                  placeholder="Enter here..."
                  value={state.oldUrl}
                  InputProps={{ readOnly }}
                  onChange={(event) =>
                    updateStateAttributes({ oldUrl: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  label="New URL *"
                  placeholder="Enter here..."
                  value={state.newUrl}
                  InputProps={{ readOnly }}
                  onChange={(event) =>
                    updateStateAttributes({ newUrl: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      {mode !== 'view' ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <DarkLoadingButton
            disableElevation
            variant="contained"
            color="inherit"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ ...defaultButtonStyles }}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </DarkLoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default AddRedirectionModal;
